import { ConversationQueryKey, updateConversationRow } from "@noa/api-client";
import {
  useAppStore,
  useChatbotState,
  useConversationName,
  useHasFirstUserMessage,
  useSessionId,
} from "@noa/client-store";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

/**
 * Run `effect` the first time `when` holds for a session, then not again until the session changes —
 * which re-arms it, so a reply in a conversation switched back to still gets its pass.
 */
const useOncePerSession = (sessionId: string, when: boolean, effect: () => void): void => {
  const ranFor = useRef<string | null>(null);

  useEffect(() => {
    if (!when || ranFor.current === sessionId) return;

    ranFor.current = sessionId;
    effect();
  }, [sessionId, when, effect]);
};

/**
 * A standing effect, not a callback. Keeps the conversation *list* in step with the chat, split by
 * who knows the answer:
 *
 * - **First exchange → invalidate, twice.** It creates the conversation server-side (the list
 *   filters to sessions with ≥2 persisted messages, so a greeting-only one is absent) and moves its
 *   row to the top of the date-sorted list — only a refetch yields that row. Once **on send**, so
 *   the row (and the active-conversation highlight, which keys off the store's session id) appears
 *   immediately rather than after the reply finishes; once again **on idle**, to reconcile, because
 *   the send-time refetch can outrun the server persisting the message.
 * - **Everything after → write the cache.** Later exchanges can't reorder the list, so only
 *   `message_count`, `last_message_at` and the generated `name` move — and the store knows all
 *   three. The name arrives mid-stream on its own SSE event, already persisted server-side, so the
 *   row can take it without asking; that is why the invalidations above don't have to race it,
 *   whichever lands first the row ends up correct. No latch needed: `updateConversationRow` no-ops
 *   on an uncached row. The count includes the in-flight assistant message, so the cached value can
 *   lead the server until the next refetch.
 *
 * The `hasUserMessage` gate on the idle pass is load-bearing — the greeting also ends in `waiting`,
 * so without it that pass is spent before the first user message.
 */
export const useSyncConversationsHistory = () => {
  const queryClient = useQueryClient();
  const chatbotState = useChatbotState();
  const sessionId = useSessionId();
  const hasUserMessage = useHasFirstUserMessage();
  const conversationName = useConversationName();
  // The count, not the array — `messages` changes identity on every streamed chunk.
  const messageCount = useAppStore((state) => state.messages.length);

  const invalidateConversationsList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ConversationQueryKey.lists });
  }, [queryClient]);

  useOncePerSession(sessionId, hasUserMessage, invalidateConversationsList);
  useOncePerSession(sessionId, hasUserMessage && chatbotState === "waiting", invalidateConversationsList);

  useEffect(() => {
    if (!sessionId || messageCount === 0) return;

    const { messages } = useAppStore.getState();

    updateConversationRow(queryClient, sessionId, {
      message_count: messages.length,
      last_message_at: messages[messages.length - 1]?.created_at ?? null,
      // A name only ever arrives on a conversation that has messages, so the gate above covers it
      // too. Omitted rather than passed as undefined, which would be spread over the row's own name.
      ...(conversationName ? { name: conversationName } : {}),
    });
  }, [messageCount, conversationName, sessionId, queryClient]);
};
